import { useCallback, useEffect, useState } from 'react'
import { ApiClientError, type Credential, type CredentialTypeDescriptor } from '../api/types.js'
import { Alert } from '../ui/Alert.js'
import { ConfirmDialog, useConfirmation } from '../ui/ConfirmDialog.js'
import { formatMoment } from '../ui/moment.js'
import { вКавычках } from '../ui/quotes.js'
import { useToaster } from '../ui/toasts.js'

/** Узкий срез клиента: экрану нужны только операции над credentials. */
export interface CredentialsClient {
  credentials(): Promise<Credential[]>
  credentialTypes(): Promise<CredentialTypeDescriptor[]>
  createCredential(type: string, name: string, data: Record<string, string>): Promise<Credential>
  removeCredential(id: string): Promise<void>
  testCredential(id: string, probeUrl: string, method?: string): Promise<{ ok: boolean; message: string }>
}

export interface CredentialsProps {
  client: CredentialsClient
  permissions: string[]
}

const CAN_WRITE = 'credentials.write'

const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

function messageOf(cause: unknown): string {
  return cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE
}

interface HeaderPair {
  name: string
  value: string
}

/**
 * Credentials арендатора. Значения секретов здесь не показываются никогда:
 * служба их не отдаёт вовсе (FR-CRED-001), а введённое в форму стирается
 * сразу после отправки (FR-UI-009) — оставлять секрет в полях значит
 * оставлять его на экране, в истории браузера и в памяти вкладки.
 *
 * Форма собирается по описаниям типов от службы: список полей, зашитый в
 * админку, расходится со схемой проверки — и человек узнаёт об этом отказом
 * при создании.
 */
export function Credentials(props: CredentialsProps) {
  const { client, permissions } = props
  const canWrite = permissions.includes(CAN_WRITE)
  const toaster = useToaster()
  const удаление = useConfirmation<Credential>()

  const [credentials, setCredentials] = useState<Credential[] | null>(null)
  const [types, setTypes] = useState<CredentialTypeDescriptor[]>([])
  const [error, setError] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [type, setType] = useState('')
  const [name, setName] = useState('')
  const [values, setValues] = useState<Record<string, string>>({})
  const [headers, setHeaders] = useState<HeaderPair[]>([{ name: '', value: '' }])
  const [creating, setCreating] = useState(false)

  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set())
  const [probeUrl, setProbeUrl] = useState('')
  const [probeFor, setProbeFor] = useState<string | null>(null)
  const [probeResult, setProbeResult] = useState<string | null>(null)

  const load = useCallback(() => {
    let cancelled = false
    Promise.all([client.credentials(), client.credentialTypes()])
      .then(([items, descriptors]) => {
        if (cancelled) return
        setCredentials(items)
        setTypes(descriptors)
        setType((current) => (current === '' ? (descriptors[0]?.type ?? '') : current))
        setError(null)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(messageOf(cause))
      })
    return () => {
      cancelled = true
    }
  }, [client])

  useEffect(load, [load])

  const descriptor = types.find((candidate) => candidate.type === type)

  /** Стирает всё введённое, включая секреты: они не должны пережить отправку. */
  function resetForm() {
    setName('')
    setValues({})
    setHeaders([{ name: '', value: '' }])
  }

  function dataOf(): Record<string, string> {
    if (descriptor?.freeForm === true) {
      return Object.fromEntries(
        headers
          .filter((pair) => pair.name.trim() !== '' && pair.value !== '')
          .map((pair) => [pair.name.trim(), pair.value]),
      )
    }
    return Object.fromEntries(
      (descriptor?.fields ?? [])
        .map((field) => [field.name, values[field.name] ?? ''] as const)
        .filter(([, value]) => value !== ''),
    )
  }

  async function create() {
    setCreating(true)
    setError(null)
    const data = dataOf()
    try {
      const created = await client.createCredential(type, name, data)
      setCredentials((items) => [created, ...(items ?? [])])
      setShowForm(false)
      resetForm()
      // Значение секрета не показывается никогда, поэтому по экрану не
      // видно, сохранилось ли оно: об этом говорит уведомление.
      toaster.succeeded(`Ключ ${вКавычках(created.name)} сохранён`)
    } catch (cause: unknown) {
      // Форма остаётся открытой, но секреты всё равно стёрты: набрать их
      // заново дешевле, чем оставить на экране (FR-UI-009).
      resetForm()
      setError(messageOf(cause))
    } finally {
      setCreating(false)
    }
  }

  async function remove(credential: Credential) {
    const id = credential.id
    удаление.cancel()
    setBusy((current) => new Set(current).add(id))
    setError(null)
    try {
      await client.removeCredential(id)
      setCredentials((items) => (items ?? []).filter((item) => item.id !== id))
      toaster.succeeded(`Ключ ${вКавычках(credential.name)} удалён`)
    } catch (cause: unknown) {
      // Отказ «на credential ссылается сценарий» приходит от службы со
      // списком сценариев в сообщении (FR-PIPE-007).
      setError(messageOf(cause))
    } finally {
      setBusy((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }

  async function test(id: string) {
    setProbeResult(null)
    setError(null)
    try {
      const outcome = await client.testCredential(id, probeUrl)
      setProbeResult(outcome.message)
    } catch (cause: unknown) {
      setError(messageOf(cause))
    }
  }

  return (
    <section aria-label="Ключи внешних служб">
      {error !== null && <Alert>{error}</Alert>}

      <div className="панель-раздела">
        <p className="панель-раздела__итог">
          {credentials === null
            ? 'Загружаем ключи…'
            : credentials.length === 0
              ? 'Ни одного ключа не заведено'
              : `Ключей внешних служб: ${credentials.length}`}
        </p>
        {canWrite && (
          <button type="button" className="btn btn--primary" onClick={() => setShowForm(!showForm)}>
            Добавить ключ
          </button>
        )}
      </div>

      {showForm && (
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            void create()
          }}
        >
          <label htmlFor="credential-тип">Тип</label>
          <select
            id="credential-тип"
            value={type}
            onChange={(event) => {
              setType(event.target.value)
              // Значения от прошлого типа к новому не относятся, а секрет
              // из них — тем более.
              resetForm()
            }}
          >
            {types.map((candidate) => (
              <option key={candidate.type} value={candidate.type}>
                {candidate.displayName}
              </option>
            ))}
          </select>

          <label htmlFor="credential-имя">Название</label>
          <input
            id="credential-имя"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />

          {descriptor?.freeForm === true
            ? headers.map((pair, index) => (
                <div key={index} className="пара-заголовка">
                  <label htmlFor={`заголовок-имя-${index}`}>Заголовок</label>
                  <input
                    id={`заголовок-имя-${index}`}
                    value={pair.name}
                    onChange={(event) => {
                      setHeaders((current) =>
                        current.map((entry, position) =>
                          position === index ? { ...entry, name: event.target.value } : entry,
                        ),
                      )
                    }}
                  />
                  <label htmlFor={`заголовок-значение-${index}`}>Значение</label>
                  <input
                    id={`заголовок-значение-${index}`}
                    type="password"
                    value={pair.value}
                    onChange={(event) => {
                      setHeaders((current) =>
                        current.map((entry, position) =>
                          position === index ? { ...entry, value: event.target.value } : entry,
                        ),
                      )
                    }}
                  />
                </div>
              ))
            : (descriptor?.fields ?? []).map((field) => (
                <div key={field.name}>
                  <label htmlFor={`credential-${field.name}`}>{field.displayName}</label>
                  <input
                    id={`credential-${field.name}`}
                    // Секрет не показывается даже тому, кто его вводит: экран
                    // видят через плечо и на демонстрации.
                    type={field.secret ? 'password' : 'text'}
                    autoComplete={field.secret ? 'new-password' : 'off'}
                    value={values[field.name] ?? ''}
                    required={field.required}
                    onChange={(event) => {
                      setValues((current) => ({ ...current, [field.name]: event.target.value }))
                    }}
                  />
                  {field.description !== undefined && (
                    <p className="form__hint">{field.description}</p>
                  )}
                </div>
              ))}

          {descriptor?.freeForm === true && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setHeaders((current) => [...current, { name: '', value: '' }])}
            >
              Ещё заголовок
            </button>
          )}

          <button type="submit" className="btn" disabled={creating || name.trim() === ''}>
            Сохранить
          </button>
        </form>
      )}

      {credentials === null && error === null && <p>Загрузка credentials…</p>}

      {credentials !== null && credentials.length === 0 && (
        <p>
          Ключей пока нет. Так платформа хранит доступы к чужим службам: ключи API, токены и
          пароли, которыми сценарий обращается наружу. Значение шифруется при сохранении и
          больше не показывается — ни здесь, ни через API. Проверить, что оно верное, можно
          кнопкой «Проверить соединение».
        </p>
      )}

      {credentials !== null && credentials.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Название</th>
              <th scope="col">Тип</th>
              <th scope="col">Создан</th>
              {canWrite && <th scope="col">Действия</th>}
            </tr>
          </thead>
          <tbody>
            {credentials.map((credential) => (
              <tr key={credential.id}>
                <td>{credential.name}</td>
                <td>
                  {types.find((candidate) => candidate.type === credential.type)?.displayName ??
                    credential.type}
                </td>
                <td>{formatMoment(credential.createdAt)}</td>
                {canWrite && (
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => setProbeFor(probeFor === credential.id ? null : credential.id)}
                    >
                      Проверить соединение
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={busy.has(credential.id)}
                      onClick={() => удаление.ask(credential)}
                    >
                      Удалить
                    </button>
                    {probeFor === credential.id && (
                      <div className="проверка">
                        <label htmlFor={`проверка-адрес-${credential.id}`}>Адрес для проверки</label>
                        <input
                          id={`проверка-адрес-${credential.id}`}
                          value={probeUrl}
                          onChange={(event) => setProbeUrl(event.target.value)}
                        />
                        <button
                          type="button"
                          className="btn"
                          disabled={probeUrl.trim() === ''}
                          onClick={() => void test(credential.id)}
                        >
                          Проверить
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Исход проверки — ответ службы как есть: он несёт код и причину, но
          не значение секрета (FR-CRED-004, FR-SEC-007). */}
      {probeResult !== null && <p className="проверка__исход">{probeResult}</p>}

      {удаление.pending !== null && (
        <ConfirmDialog
          title={`Удалить ключ ${вКавычках(удаление.pending.name)}?`}
          confirmLabel="Удалить"
          busy={busy.has(удаление.pending.id)}
          onCancel={удаление.cancel}
          onConfirm={() => {
            if (удаление.pending !== null) void remove(удаление.pending)
          }}
        >
          <p>
            Узлы сценариев, которые обращаются с этим ключом наружу, перестанут работать: службе
            больше нечем будет им представиться.
          </p>
          <p>
            Значение ключа восстановить нельзя — оно хранится зашифрованным и не отдаётся даже
            нам. Вернуть доступ можно только заведя ключ заново.
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
